import React, { useEffect, useState } from "react";
import { apiClient, formatApiError } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import {
  ShieldCheck, Loader2, RefreshCw, CheckCircle2, XCircle, AlertTriangle,
  Eye, User as UserIcon, Building2, FileText, Image as ImageIcon,
} from "lucide-react";

/**
 * KycTab — admin review queue for KYC submissions.
 * Filters by status (pending / approved / rejected / all) and type.
 * Inline approve / reject (with notes). Doc preview opens in a modal.
 */
export default function KycTab() {
  const { lang } = useI18n();
  const [status, setStatus] = useState("pending");
  const [type, setType] = useState("");
  const [data, setData] = useState({ items: [], counts: { pending: 0, approved: 0, rejected: 0 } });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [detail, setDetail] = useState(null);
  const [decisionFor, setDecisionFor] = useState(null); // {id, action: "approve"|"reject"}
  const [notes, setNotes] = useState("");
  const [acting, setActing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const reload = async () => {
    setBusy(true); setErr(null);
    try {
      const params = { status };
      if (type) params.kyc_type = type;
      const { data } = await apiClient.get("/admin/kyc/queue", { params });
      setData(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  useEffect(() => { reload(); }, [status, type]);

  const openDetail = async (id) => {
    try {
      const { data } = await apiClient.get(`/admin/kyc/${id}`);
      setDetail(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const submitDecision = async () => {
    if (!decisionFor) return;
    if (decisionFor.action === "reject" && !notes.trim()) {
      setErr(lang === "ar" ? "أضف سبب الرفض" : "Please add a rejection reason");
      return;
    }
    setActing(true); setErr(null);
    try {
      await apiClient.post(`/admin/kyc/${decisionFor.id}/${decisionFor.action}`, { notes: notes.trim() || null });
      setDecisionFor(null); setNotes(""); setDetail(null);
      await reload();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setActing(false); }
  };

  return (
    <div className="space-y-5" data-testid="kyc-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-[#0a0a0a] inline-flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-[#8a6f2e]" />
            {lang === "ar" ? "مركز الثقة والأمان (KYC)" : "Trust & Safety (KYC)"}
          </h2>
          <p className="text-sm text-[#6b6b6b] mt-1">
            {lang === "ar" ? "راجع وثائق الأفراد والتجار يدوياً واعتمد أو ارفض الطلبات." : "Manually review individual and business documents. Approve or reject submissions."}
          </p>
        </div>
        <button onClick={reload} disabled={busy} className="btn-ghost text-sm" data-testid="kyc-refresh">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {lang === "ar" ? "تحديث" : "Refresh"}
        </button>
      </div>

      {/* Status pills */}
      <div className="flex flex-wrap gap-2" data-testid="kyc-status-filter">
        {[
          { id: "pending", count: data.counts.pending, color: "#8a6f2e", ar: "قيد المراجعة", en: "Pending" },
          { id: "approved", count: data.counts.approved, color: "#0a4f3a", ar: "موافق عليها", en: "Approved" },
          { id: "rejected", count: data.counts.rejected, color: "#b91c1c", ar: "مرفوضة", en: "Rejected" },
          { id: "all", count: data.counts.pending + data.counts.approved + data.counts.rejected, color: "#3a3a3a", ar: "الكل", en: "All" },
        ].map((s) => (
          <button
            key={s.id}
            onClick={() => setStatus(s.id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all border ${status === s.id ? "shadow-md" : "opacity-70 hover:opacity-100"}`}
            style={status === s.id
              ? { backgroundColor: s.color, borderColor: s.color, color: "white" }
              : { backgroundColor: "white", borderColor: "#0a0a0a14", color: "#3a3a3a" }}
            data-testid={`kyc-filter-${s.id}`}
          >
            {lang === "ar" ? s.ar : s.en} ({s.count})
          </button>
        ))}
      </div>

      {/* Type filter */}
      <div className="flex flex-wrap gap-2">
        {[
          { id: "", ar: "جميع الأنواع", en: "All types" },
          { id: "individual", ar: "فرد", en: "Individual" },
          { id: "business", ar: "تاجر / شركة", en: "Business" },
        ].map((t) => (
          <button
            key={t.id || "all-types"}
            onClick={() => setType(t.id)}
            className={`px-3 py-1 rounded-full text-xs border ${type === t.id ? "border-[#c9a55c] bg-[#fafaf7] text-[#0a0a0a]" : "border-[#0a0a0a14] text-[#6b6b6b] hover:text-[#0a0a0a]"}`}
            data-testid={`kyc-type-${t.id || "all"}`}
          >
            {lang === "ar" ? t.ar : t.en}
          </button>
        ))}
      </div>

      {err && <div className="rounded-xl p-3 text-sm text-red-700 border border-red-200 bg-red-50" data-testid="kyc-error">{String(err)}</div>}

      {/* Queue list */}
      <div className="space-y-3">
        {busy && data.items.length === 0 ? (
          <div className="text-center py-12 text-[#6b6b6b]"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : data.items.length === 0 ? (
          <div className="surface rounded-2xl p-12 text-center text-[#6b6b6b]" data-testid="kyc-empty">
            {lang === "ar" ? "لا توجد طلبات في هذه الحالة." : "No submissions in this state."}
          </div>
        ) : data.items.map((it) => (
          <div key={it.id} className="surface rounded-2xl p-5" data-testid={`kyc-item-${it.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 text-sm">
                  {it.kyc_type === "business" ? <Building2 className="w-4 h-4 text-[#8a6f2e]" /> : <UserIcon className="w-4 h-4 text-[#8a6f2e]" />}
                  <span className="font-semibold text-[#0a0a0a]">
                    {it.kyc_type === "business" ? it.business_name_ar : it.full_name_ar}
                  </span>
                  <StatusPill status={it.status} lang={lang} />
                </div>
                <div className="text-xs text-[#6b6b6b] mt-1.5">
                  {it.user?.email} · {lang === "ar" ? "هوية:" : "ID:"} <code className="bg-[#fafaf7] px-1 rounded">{it.national_id || it.cr_number}</code> · {new Date(it.submitted_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB")}
                </div>
                {it.review_notes && (
                  <p className="text-xs text-[#525252] mt-2 italic">
                    {lang === "ar" ? "ملاحظة:" : "Note:"} {it.review_notes}
                  </p>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => openDetail(it.id)} className="btn-ghost text-xs" data-testid={`kyc-view-${it.id}`}>
                  <Eye className="w-3.5 h-3.5" /> {lang === "ar" ? "عرض الوثائق" : "View documents"}
                </button>
                {it.status === "pending" && (
                  <>
                    <button onClick={() => { setDecisionFor({ id: it.id, action: "approve" }); setNotes(""); }} className="btn-primary text-xs" data-testid={`kyc-approve-${it.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> {lang === "ar" ? "اعتماد" : "Approve"}
                    </button>
                    <button onClick={() => { setDecisionFor({ id: it.id, action: "reject" }); setNotes(""); }} className="btn-ghost text-xs text-red-700 border-red-200 hover:bg-red-50" data-testid={`kyc-reject-${it.id}`}>
                      <XCircle className="w-3.5 h-3.5" /> {lang === "ar" ? "رفض" : "Reject"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Detail modal */}
      {detail && (
        <Modal onClose={() => setDetail(null)} testid="kyc-detail-modal">
          <h3 className="font-display text-xl mb-3 inline-flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#8a6f2e]" />
            {lang === "ar" ? "تفاصيل الطلب" : "Submission detail"}
          </h3>
          <DetailContent sub={detail.submission} user={detail.user} lang={lang} onPreview={setPreviewUrl} />
        </Modal>
      )}

      {/* Decision modal */}
      {decisionFor && (
        <Modal onClose={() => { setDecisionFor(null); setNotes(""); }} testid="kyc-decision-modal">
          <h3 className="font-display text-xl mb-2 inline-flex items-center gap-2">
            {decisionFor.action === "approve" ? <CheckCircle2 className="w-5 h-5 text-[#0a4f3a]" /> : <AlertTriangle className="w-5 h-5 text-red-700" />}
            {decisionFor.action === "approve"
              ? (lang === "ar" ? "اعتماد الطلب" : "Approve submission")
              : (lang === "ar" ? "رفض الطلب" : "Reject submission")}
          </h3>
          <p className="text-sm text-[#525252] mb-3">
            {decisionFor.action === "approve"
              ? (lang === "ar" ? "سيتم توثيق حساب المستخدم وإظهار شارة \"موثّق\"." : "The user account will be marked verified.")
              : (lang === "ar" ? "اشرح للمستخدم لماذا تم الرفض حتى يتمكن من إعادة الإرسال." : "Explain why so the user can resubmit.")}
          </p>
          <label className="block text-xs font-semibold text-[#3a3a3a] mb-1">
            {decisionFor.action === "reject"
              ? (lang === "ar" ? "سبب الرفض (مطلوب)" : "Reason (required)")
              : (lang === "ar" ? "ملاحظات (اختيارية)" : "Notes (optional)")}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            maxLength={600}
            className="input-dark"
            data-testid="kyc-notes"
            placeholder={decisionFor.action === "reject"
              ? (lang === "ar" ? "مثال: صورة الهوية غير واضحة" : "e.g. ID image is blurry")
              : ""}
          />
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => { setDecisionFor(null); setNotes(""); }} className="btn-ghost text-sm">
              {lang === "ar" ? "إلغاء" : "Cancel"}
            </button>
            <button
              onClick={submitDecision}
              disabled={acting || (decisionFor.action === "reject" && !notes.trim())}
              className="btn-primary text-sm"
              style={decisionFor.action === "reject" ? { backgroundColor: "#b91c1c", borderColor: "#b91c1c" } : {}}
              data-testid="kyc-confirm-decision"
            >
              {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : (decisionFor.action === "approve" ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />)}
              {decisionFor.action === "approve" ? (lang === "ar" ? "اعتماد" : "Approve") : (lang === "ar" ? "رفض" : "Reject")}
            </button>
          </div>
        </Modal>
      )}

      {/* Image preview modal */}
      {previewUrl && (
        <div
          onClick={() => setPreviewUrl(null)}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 cursor-zoom-out"
          style={{ backgroundColor: "rgba(10,10,10,0.9)" }}
          data-testid="kyc-image-preview"
        >
          <img src={previewUrl.startsWith("http") ? previewUrl : `${process.env.REACT_APP_BACKEND_URL}${previewUrl}`}
            alt="document"
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-2xl shadow-2xl" />
        </div>
      )}
    </div>
  );
}

function StatusPill({ status, lang }) {
  const map = {
    pending: { bg: "rgba(201,165,92,0.15)", fg: "#8a6f2e", ar: "قيد المراجعة", en: "Pending" },
    approved: { bg: "rgba(10,79,58,0.12)", fg: "#0a4f3a", ar: "موافق", en: "Approved" },
    rejected: { bg: "rgba(185,28,28,0.10)", fg: "#b91c1c", ar: "مرفوض", en: "Rejected" },
  };
  const c = map[status] || map.pending;
  return <span className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5" style={{ backgroundColor: c.bg, color: c.fg }}>{lang === "ar" ? c.ar : c.en}</span>;
}

function Modal({ children, onClose, testid }) {
  return (
    <div onClick={onClose} className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(10,10,10,0.7)" }} data-testid={testid}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto p-6 shadow-2xl">
        {children}
      </div>
    </div>
  );
}

function DetailContent({ sub, user, lang, onPreview }) {
  if (!sub) return null;
  const fields = sub.kyc_type === "individual"
    ? [
        ["full_name_ar", lang === "ar" ? "الاسم" : "Name"],
        ["national_id", lang === "ar" ? "رقم الهوية" : "National ID"],
        ["nationality", lang === "ar" ? "الجنسية" : "Nationality"],
        ["dob", lang === "ar" ? "تاريخ الميلاد" : "DOB"],
      ]
    : [
        ["business_name_ar", lang === "ar" ? "الاسم (عربي)" : "Name (AR)"],
        ["business_name_en", lang === "ar" ? "الاسم (إنجليزي)" : "Name (EN)"],
        ["cr_number", lang === "ar" ? "السجل التجاري" : "CR #"],
        ["vat_number", lang === "ar" ? "الرقم الضريبي" : "VAT #"],
        ["owner_national_id", lang === "ar" ? "هوية المالك" : "Owner ID"],
        ["address", lang === "ar" ? "العنوان" : "Address"],
      ];
  const docs = sub.kyc_type === "individual"
    ? [["id_front_url", "ID Front"], ["id_back_url", "ID Back"], ["selfie_url", "Selfie"]]
    : [["cr_document_url", "CR Doc"], ["vat_document_url", "VAT Cert"]];

  return (
    <>
      {user && (
        <div className="text-xs text-[#6b6b6b] mb-4 pb-3 border-b border-[#0a0a0a]/[0.08]">
          <strong>{user.name}</strong> · {user.email} · <span className="pill text-[10px]">{user.role}</span>
        </div>
      )}
      <dl className="grid sm:grid-cols-2 gap-3 mb-5">
        {fields.map(([k, label]) => (
          <div key={k}>
            <dt className="text-[10px] uppercase tracking-widest text-[#8a8a8a]">{label}</dt>
            <dd className="text-sm text-[#0a0a0a] mt-0.5">{sub[k] || "—"}</dd>
          </div>
        ))}
      </dl>
      <h4 className="font-semibold text-sm mb-2">{lang === "ar" ? "الوثائق" : "Documents"}</h4>
      <div className="grid grid-cols-3 gap-2">
        {docs.map(([k, label]) =>
          sub[k] ? (
            <button key={k} onClick={() => onPreview(sub[k])} className="rounded-lg border border-[#0a0a0a]/[0.08] p-2 text-xs hover:border-[#c9a55c] transition-colors" data-testid={`kyc-doc-${k}`}>
              <ImageIcon className="w-5 h-5 mx-auto mb-1 text-[#8a6f2e]" />
              {label}
            </button>
          ) : (
            <div key={k} className="rounded-lg border border-dashed border-[#0a0a0a]/[0.08] p-2 text-xs text-[#a8a8a8] text-center">
              — {label} —
            </div>
          )
        )}
      </div>
    </>
  );
}
